import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SectionTitle from '~/components/section-title/index';
import { useAtomValue } from 'jotai';
import { $CurrentArticleRelatedHashtag } from '@article/shared/atoms/current-article-related-hashtag';
import _uniq from 'lodash/uniq';
import { $Locale, $UserAgent } from '@lemon8/web-app-shared/atom';
import { TopicCard } from '~/components/topic-card';
import { SeeMore } from '~/components/see-more';
import { $CurrentArticle } from '@article/shared/atoms/current-article';
import { useViewTracking } from '~/utils/use-view-tracking';
import { ModuleType } from '~/utils/module-type';
import { $CurrentPageModule } from '~/models/current-page-module';
import { ScopedProvider } from '@lemon8/web-app-shared/ssr';
import { ArticleDetailUtil } from 'shared/models';
import { $CurrentPositionPrefix } from '~/models/current-position-prefix';
import { ClickPositionPrefix } from '~/utils/click-position';
import { $CurrentModuleRank } from '~/models/current-module-rank';
import { $IsMobileUser } from '~/utils/is-mobile-user';
import { SeeMoreBanner } from '~/components/see-more-banner';

import './style.scss';

const queryLengthFilter = (region: string) => {
  if (region === 'jp') {
    return (str: string) => str.length <= 30;
  } else if (region === 'th') {
    return (str: string) => str.length <= 100;
  } else {
    return (str: string) => str.length <= 60;
  }
};

export const ArticleRelatedTopicSection: FC = () => {
  const { t } = useTranslation();

  const { region } = useAtomValue($Locale);
  const { isBot } = useAtomValue($UserAgent);
  const isMobileUser = useAtomValue($IsMobileUser);
  const currentArticle = useAtomValue($CurrentArticle);
  const articleAvailable = ArticleDetailUtil.isArticleAvailable(currentArticle);
  const { items, sugWords, gptExtendWords } = useAtomValue($CurrentArticleRelatedHashtag);
  const [pageIndex, setPageIndex] = useState(0);
  const PAGE_NUMBER = isBot ? Number.MAX_VALUE : 10;

  const words = useMemo(() => {
    const { longTailWords, highVolumeWords, relatedSEOWords } = gptExtendWords;

    const topicNameSet = new Set(items.map((item) => item.topicName));

    return _uniq([...relatedSEOWords, ...longTailWords, ...highVolumeWords, ...sugWords])
      .filter((str) => !topicNameSet.has(str))
      .filter(queryLengthFilter(region));
  }, [gptExtendWords, items]);

  const topics = useMemo(() => [...items, ...words], [items, words]);

  const handleClickSeeMore = useCallback(() => {
    setPageIndex((p) => p + 1);
  }, []);

  const topicSectionObservedRef = useViewTracking({
    onView: useCallback(() => {
      window.Tea('wap_module_show', {
        current_page_module: ModuleType.related_topic,
      });
    }, []),
  });

  const renderBottomText = (): JSX.Element | null => {
    if (topics.length > (pageIndex + 1) * PAGE_NUMBER) {
      if (isMobileUser) {
        return (
          <div className="article-related-topic-see-more">
            <SeeMoreBanner onClickSeeMore={handleClickSeeMore} />
          </div>
        );
      } else {
        return <SeeMore onClickSeeMore={handleClickSeeMore} />;
      }
    } else {
      return null;
    }
  };

  if (
    topics.length === 0 ||
    (articleAvailable && ArticleDetailUtil.isTranslatedArticleToShow(currentArticle, region))
  ) {
    return null;
  }

  return (
    <section id="article-related-topic" ref={topicSectionObservedRef}>
      <ScopedProvider
        atomsWithHydration={[
          [$CurrentPageModule, ModuleType.related_topic],
          [$CurrentPositionPrefix, ClickPositionPrefix.none],
          [$CurrentModuleRank, -1],
        ]}
      >
        <SectionTitle title={t('wap_seo_recoomend_topic')} />
        {topics.slice(0, (pageIndex + 1) * PAGE_NUMBER).map((hashtag) => (
          <TopicCard key={typeof hashtag === 'string' ? hashtag : hashtag.topicId} topic={hashtag} />
        ))}
        {renderBottomText()}
      </ScopedProvider>
    </section>
  );
};
